import { useState, FormEvent } from 'react';
import { ImageUploader } from '../ui/ImageUploader';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface ActivityFormProps {
	selectedDate: string;
	onSuccess?: (message: string) => void;
}

export function ActivityForm({ selectedDate, onSuccess }: ActivityFormProps) {
	const [activityName, setActivityName] = useState('');
	const [duration, setDuration] = useState('');
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingBlobs, setPendingBlobs] = useState<Blob[]>([]);
	const [uploaderKey, setUploaderKey] = useState(0);

	const resetForm = () => {
		setActivityName('');
		setDuration('');
		setNotes('');
		setPendingBlobs([]);
		setUploaderKey((k) => k + 1);
	};

	const uploadImages = async (entryId: string, blobs: Blob[]) => {
		const ext = await getPreferredExtension();
		for (const blob of blobs) {
			const result = await convertToAVIF(blob);
			const formData = new FormData();
			formData.append('file', result.blob, `image-${Date.now()}.${ext}`);
			formData.append('entryType', 'activity');
			formData.append('entryId', entryId);
			formData.append('width', result.width.toString());
			formData.append('height', result.height.toString());
			const res = await fetch(apiUrl('/api/images'), {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			if (!res.ok) {
				throw new Error('Gagal mengunggah foto. Silakan coba lagi.');
			}
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		if (!activityName.trim()) {
			setError('Nama aktivitas harus diisi');
			setIsSubmitting(false);
			return;
		}

		const durationValue = parseInt(duration, 10);
		if (isNaN(durationValue) || durationValue <= 0) {
			setError('Durasi harus berupa angka positif');
			setIsSubmitting(false);
			return;
		}

		try {
			const res = await fetch(apiUrl('/api/activities'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					date: selectedDate,
					activityName: activityName.trim(),
					durationMinutes: duration,
					notes: notes || undefined,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			if (pendingBlobs.length > 0) {
				await uploadImages(data.id, pendingBlobs);
			}

			onSuccess?.('Aktivitas berhasil dicatat!');
			resetForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<h2 className="text-lg font-semibold text-gray-900">Catat Aktivitas</h2>

			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
					{error}
				</div>
			)}

			<div>
				<label
					htmlFor="activity-name"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Nama Aktivitas <span className="text-red-500">*</span>
				</label>
				<div className="flex flex-wrap gap-2">
					<input
						type="text"
						id="activity-name"
						value={activityName}
						onChange={(e) => setActivityName(e.target.value)}
						placeholder="Contoh: Merajut, Merangkai bunga"
						required
						disabled={isSubmitting}
						className="flex-1 min-w-[150px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
					/>
					<div className="flex gap-1">
						{['Merajut', 'Merangkai bunga'].map((name) => (
							<button
								key={name}
								type="button"
								onClick={() => setActivityName(name)}
								disabled={isSubmitting}
								className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{name}
							</button>
						))}
					</div>
				</div>
			</div>

			<div>
				<label
					htmlFor="activity-duration"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Durasi (menit) <span className="text-red-500">*</span>
				</label>
				<input
					type="number"
					id="activity-duration"
					value={duration}
					onChange={(e) => setDuration(e.target.value)}
					min="1"
					placeholder="Contoh: 60"
					required
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			<div>
				<label
					htmlFor="activity-notes"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Catatan (opsional)
				</label>
				<textarea
					id="activity-notes"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					rows={3}
					placeholder="Catatan tambahan..."
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Foto (opsional)
				</label>
				<ImageUploader
					key={uploaderKey}
					entryType="activity"
					onPendingBlobsChange={setPendingBlobs}
					disabled={isSubmitting}
				/>
			</div>

			<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
				<button
					type="submit"
					disabled={isSubmitting}
					className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
				>
					{isSubmitting ? 'Menyimpan...' : 'Simpan'}
				</button>
			</div>
		</form>
	);
}
